import { useContext, useState, useEffect } from 'react';
import { gql } from '@apollo/client';

import { Db } from '../../../services/Db';
import { AppContext } from '../../../App.context';
import {
	Article,
	Issue,
	getUserRole,
	clone,
	getAccessToken,
	getImagesFromIssue,
} from '../../../constants';
import { KV } from '../../../services/kv/KV';
import config from '../../../config';
import { sortIssuesReleaseDate } from '../../../hooks/use-issue';

export function useLoad(): GetIssuesResponse {
	const { appKey, apolloClient, onlineStatus, db, kv, sw } =
		useContext(AppContext);
	const [issues, setIssues] = useState<Array<Issue>>([]);
	const [articles, setArticles] = useState<Array<Article>>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (onlineStatus === null) {
			return;
		}
		if (isLoading === false) {
			return;
		}
		if (!db) {
			return;
		}
		const props = {
			apolloClient,
			appKey,
			onlineStatus,
			sw,
			db,
			kv,
		};

		getData(props)
			.then(({ issues, articles }) => {
				setIssues(issues);
				setArticles(articles);
			})
			.catch((err) => {
				console.error(err);
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [apolloClient, onlineStatus, isLoading, sw, db]);
	return {
		issues,
		articles,
		isLoading,
		error,
	};
}

export interface GetIssuesResponse {
	issues: Array<Issue>;
	articles: Array<Article>;
	isLoading: boolean;
	error?: Error | null;
}

async function getData(args: GetIssuesArgs): Promise<Data> {
	const { onlineStatus, db } = args;
	if (onlineStatus) {
		const { issues, articles } = await getRemoteData(args);
		await db.saveIssues(issues);
		await db.saveFeatureArticles(articles);
		return {
			issues: issues.sort(sortIssuesReleaseDate),
			articles: articles.sort(sortByCreatedDate),
		};
	}

	const cachedIssues = await db.getIssues();
	const cachedFeatureArticles = await db.getFeatureArticles();
	return {
		issues: cachedIssues.sort(sortIssuesReleaseDate),
		articles: cachedFeatureArticles.sort(sortByCreatedDate),
	};
}

async function getRemoteData(args: GetIssuesArgs): Promise<Data> {
	const { apolloClient, appKey, sw, kv } = args;
	const fetchPolicyKey = 'remote-magazine-data';

	const fetchPolicy = kv.get(fetchPolicyKey, 'no-cache');
	if (!kv.has(fetchPolicyKey)) {
		kv.set(
			fetchPolicyKey,
			'cache-first',
			config?.app?.cache?.ttl?.getAllIssues ?? undefined,
		);
	}

	const headers = {
		'app-key': appKey,
		Authorization: getAccessToken(),
		'app-user-role': getUserRole(),
	};

	const result = await apolloClient.query({
		query: GET_ISSUES_AND_FEATURE_ARTICLES,
		fetchPolicy,
		headers,
	});

	const { issues, articles } = clone(result.data);
	if (issues['__typename']) {
		delete issues['__typename'];
	}

	if (sw) {
		const images: Set<string> = new Set();
		getImagesFromIssue(images, issues);
		if (images.size) {
			sw.postMessage({
				type: 'DOWNLOAD_BACKGROUND_IMAGES',
				payload: {
					images: [...images],
				},
			});
		}
	}

	return {
		issues,
		articles,
	};
}

export interface GetIssuesArgs {
	appKey: string;
	apolloClient: any;
	onlineStatus: boolean;
	sw: ServiceWorker | null;
	db: Db;
	kv: KV;
}

interface Data {
	issues: Array<Issue>;
	articles: Array<Article>;
}

function sortByCreatedDate(a: Article, b: Article): any {
	return (
		new Date(`${b.created}`).valueOf() - new Date(`${a.created}`).valueOf()
	);
}

export function useGetIssue(id: string): GetIssueResponse {
	const { appKey, apolloClient, onlineStatus, sw, kv } =
		useContext(AppContext);
	const [issue, setIssue] = useState<Issue | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (onlineStatus === null) {
			return;
		}
		if (isLoading === false) {
			return;
		}

		const props = {
			id,
			apolloClient,
			appKey,
			onlineStatus,
			sw,
			kv,
		};

		getIssue(props)
			.then((issue) => {
				setIssue(issue);
			})
			.catch((err) => {
				console.error(err);
				setError(err);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [apolloClient, onlineStatus, isLoading, sw]);
	return {
		issue,
		isLoading,
		error,
	};
}

interface GetIssueResponse {
	issue: Issue | null;
	isLoading: boolean;
	error?: Error | null;
}

async function getIssue(args: GetIssueArgs) {
	const { id, apolloClient, appKey } = args;
	const result = await apolloClient.query({
		query: GET_ISSUE,
		fetchPolicy: 'no-cache',
		headers: {
			'app-key': appKey,
			Authorization: getAccessToken(),
		},
		variables: {
			id,
		},
	});

	const { issue } = result.data;

	return issue;
}

interface GetIssueArgs {
	id: string;
	appKey: string;
	apolloClient: any;
	kv: KV;
}

export const GET_ISSUE = gql`
	query GetIssue($id: ID!) {
		issue(id: $id) {
			id
			name
			description
			articles {
				id
				ArticleID
				thumbnail
				keywords
				style
				role
				styleProperties
				name
				index
				channel
				slug
				description
				isFeatured
				created
				updated
			}
		}
	}
`;

export const GET_ISSUES_AND_FEATURE_ARTICLES = gql`
	{
		issues {
			id
			name
			description
			cover
			externalUrl
			price
			index
			slug
			currency
			data
			updated
			created
			isPurchased
			releaseDate
		}
		articles(isFeatured: true) {
			id
			name
			IssueID
			slug
			thumbnail
			featureImage
			index
			isFeatured
			created
			updated
		}
	}
`;
